import { PlusOutlined } from "@ant-design/icons";
import { Alert, Badge, Button, ConfigProvider, Popconfirm, Radio, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";

import { cancelJob } from "api/job";
import { LoadingSpinner } from "components/LoadingSpinner";
import { getPaginationProps } from "components/PaginationProps";
import { SearchComponent } from "components/SearchComponent";
import dayjs from "dayjs";
import { CreateJobModal } from "modals/CreateJobModal";
import { ViewJob } from "./ViewJob";
import { useGetIdentity } from "@refinedev/core";
import { useSystemMaintenance } from "hooks/useSystemMaintenance";
import { MaintenanceAlert } from "components/MaintenanceAlert";
import { rescaleJobRunningPercentage } from "utils/rescaleJobRunningPercentage";
import { STATUS_MAP_COLOR } from "constants/statusUtils";
import { useTable } from "@refinedev/antd";

const STATUS_MAP = {
  0: "RUNNING",
  1: "TERMINATING",
  2: "COMPLETED",
  3: "CANCELLED",
  4: "UNRUNNABLE",
  5: "IN QUEUE"
};

interface DataType {
  job_name: string;
  JobName: string;
  StartDate: string;
  RunTime: string;
  status: number;
  time: string;
  ation: string;
  id: string;
  result: string;
  elapsedTime: number;
  createdAt: Date;

  job_type?: string;
  n_photons?: number;
  n_modes?: number;
  max_events?: number;
  max_time_ms?: number;
  unitary?: number[][][];
  configurations?: any[];
  self_uri?: string;
  job_runs_uri?: string;
}

export const Jobs: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showNewJobs, setShowNewJobs] = useState(true);
  const { refetch: refetchUser } = useGetIdentity();

  const { tableProps, setFilters, filters, tableQueryResult } = useTable<DataType>({
    liveMode: "auto",
    resource: "jobs/admin",
    pagination: {
      mode: "server"
    },
    syncWithLocation: true,
    sorters: {
      initial: [
        {
          field: "createdAt",
          order: "desc"
        }
      ]
    }
  });

  const cancel = async (jobId: string) => {
    setIsLoading(true);
    await cancelJob(jobId);
    tableQueryResult.refetch();
    setIsLoading(false);
  };

  const [searchQuery, setSearchQuery] = useState("");

  const isSystemMaintenance = useSystemMaintenance();

  useEffect(() => {
    (async () => {
      setLoadingJobs(true);
      tableQueryResult.refetch();
      setLoadingJobs(false);
    })();
  }, []);

  const columns: ColumnsType<DataType> = [
    {
      title: "Job Name",
      dataIndex: "job_name",
      key: "JobName",
      align: "center",
      width: 140,
      ellipsis: true
    },
    {
      title: "Created Date",
      width: "15%",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render(_, record: any) {
        return `${new Date(record.createdAt).toLocaleDateString()} ${new Date(
          record.createdAt
        ).toLocaleTimeString()}`;
      }
    },
    {
      title: "Executed/Estimated Run Time",
      dataIndex: "elapsedTime",
      key: "RunTime",
      width: 30,
      align: "center",
      render: (value, record) => {
        return <div>{record?.status === 4 ? 0 : (Number(value) / 60).toFixed(2)} Min</div>;
      }
    },
    {
      title: "Percentage(%)",
      dataIndex: "jobCompletionPercentage",
      key: "jobCompletionPercentage",
      width: 20,
      align: "center",
      render: (value, record) => (
        <p>
          {record.status >= 4
            ? "0.00"
            : record.status === 0
              ? rescaleJobRunningPercentage(value?.toFixed(2))
              : value?.toFixed(2) || "0.00"}
        </p>
      )
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      dataIndex: "status",
      align: "center",
      render: (value, record) => (
        <Badge
          size="small"
          style={{
            color: "#fff",
            fontSize: "12px",
            border: "1px solid #312E2E",
            borderRadius: "16px",
            height: "22px",
            paddingLeft: "8px",
            paddingRight: "8px",
            paddingTop: "8px",
            paddingBottom: "8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginLeft: "auto",
            marginRight: "auto"
          }}
          color={STATUS_MAP_COLOR[value]}
          text={
            <span
              style={{
                fontSize: "12px",
                fontWeight: 500,
                color: STATUS_MAP_COLOR[value],
                textWrap: "nowrap"
              }}>
              {value === 2 && !record.result ? "UPLOADING RESULT" : STATUS_MAP[value as 0 | 1]}
            </span>
          }
        />
      )
    },
    {
      title: "(Expected) Completion Date",
      key: "completionDate",
      width: 140,
      dataIndex: "completionDate",
      align: "center",
      render(_, record: any) {
        return record.completionDate && record.status !== 3 // should not be cancelled
          ? `${new Date(record.completionDate).toLocaleDateString()} ${new Date(
              record.completionDate
            ).toLocaleTimeString()}`
          : null;
      }
    },
    {
      title: "Action",
      key: "action",
      dataIndex: "action",
      align: "right",
      width: 120,
      render: (_, record) => {
        if (record.status === 2) {
          return (
            <ViewJob jobId={record.id} jobName={record.job_name} elapsedTime={record.elapsedTime} />
          );
        } else if (record.status !== 3 && record.status !== 4) {
          return (
            <Popconfirm
              title="Are you sure you want to cancel this job?"
              okText={"Yes"}
              okButtonProps={{
                loading: isLoading
              }}
              cancelText={"No"}
              onConfirm={async () => {
                await cancel(record.id);
                await refetchUser();
              }}
              style={{
                background: "transparent"
              }}>
              <div
                style={{
                  color: "#FF5A17",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  background: "transparent"
                }}>
                Cancel Job
              </div>
            </Popconfirm>
          );
        } else if (record.status === 4) {
          return record.result;
        }
      }
    }
  ];

  const showModal = () => {
    setIsModalOpen(true);
  };

  if (loadingJobs) return <LoadingSpinner />;

  return (
    <>
      {<MaintenanceAlert />}
      <div>
        <Typography.Title style={{ fontWeight: "normal", fontSize: "30px" }} level={1}>
          Jobs overview
        </Typography.Title>
        <Typography.Text>Get an overview of all old and new jobs</Typography.Text>
        <Radio.Group
          style={{ marginBottom: 16, marginTop: 16, display: "flex" }}
          defaultValue={"new"}
          onChange={(e) => setShowNewJobs(e.target.value === "new")}>
          <Radio.Button
            style={{
              width: "98px",
              color: "#CECFD2",
              fontSize: "14px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            value="new">
            New jobs
          </Radio.Button>
          <Radio.Button
            style={{
              width: "98px",
              color: "#CECFD2",
              fontSize: "14px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            value="old">
            Old jobs
          </Radio.Button>
        </Radio.Group>
        {isModalOpen && (
          <CreateJobModal
            fetchJobData={async () => {
              tableQueryResult.refetch?.();
            }}
            isModalOpen={isModalOpen}
            setModalOpen={setIsModalOpen}
          />
        )}
        <SearchComponent
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          placeholder="Search Jobs">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginLeft: 8
            }}>
            {/* <DatePicker.RangePicker
                  // onChange={(dates) => {
                  // 	setSelectedDateRange([
                  // 		dates[0] ? dayjs(dates[0]?.toDate()) : null,
                  // 		dates[1] ? dayjs(dates[1]?.toDate()) : null,
                  // 	]);
                  // 	filterRows();
                  // }}
                  // value={selectedDateRange}
                  style={{ marginRight: 16 }}
                /> */}
            {!isSystemMaintenance && (
              <Button
                style={{ height: "40px", border: "none" }}
                icon={<PlusOutlined />}
                onClick={() => {
                  showModal();
                }}>
                Create job
              </Button>
            )}
          </div>
        </SearchComponent>
        <Table
          {...tableProps}
          style={{ marginTop: "30px" }}
          // scroll={{ x: "max-content" }}
          columns={columns}
          dataSource={(tableProps.dataSource as DataType[])?.filter((job: DataType) => {
            const daysDifference = dayjs(new Date()).diff(dayjs(job.createdAt), "days");
            const filterByJobType =
              (showNewJobs && daysDifference <= 7) || (!showNewJobs && daysDifference > 7);
            return (
              filterByJobType &&
              (!searchQuery ||
                job?.job_name?.trim()?.toLowerCase()?.includes(searchQuery.trim().toLowerCase()))
            );
          })}
          pagination={getPaginationProps()}
        />
      </div>
    </>
  );
};
